import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check } from "@phosphor-icons/react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
    fetchCustomPropertyNote,
    saveCustomPropertyNote,
    removeCustomPropertyNote,
} from "@/lib/api";

const propertyNoteKey = (id) => ["property-note", id];

const extractNote = (res) => {
    if (!res) return "";
    if (typeof res === "string") return res;
    const d = res.data ?? res;
    if (typeof d === "string") return d;
    return d?.note ?? d?.text ?? d?.noteText ?? d?.description ?? "";
};

const QUICK_NOTES = [
    "Satılır, aktualdır",
    "Qiymətdə razılaşmaq olur",
    "Baxış təyin olundu",
    "Maraqlanır, özü zəng edəcək",
    "Sənədləri qaydasındadır",
    "Vasitəçidir (Sahibkar deyil)",
    "Komissiyaya razıdır",
    "Əlavə şəkil və məlumat göndərəcək",
    "Sonra zəng et",
    "Zəng açmadı",
    "Satılıb",
    "Qiymət razı gəlmədi",
    "Nömrə səhvdir",
];

const MAX_NOTE_LENGTH = 1000;

export const PropertyNoteDialog = ({ propertyId, open, onOpenChange, onNoteChange }) => {
    const queryClient = useQueryClient();
    const [note, setNote] = useState("");
    const [hasNote, setHasNote] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const busy = loading || saving || deleting;

    useEffect(() => {
        if (!open || !propertyId) return;

        let cancelled = false;
        setNote("");
        setHasNote(false);
        setLoading(true);

        fetchCustomPropertyNote(propertyId)
            .then((res) => {
                if (cancelled) return;
                const text = extractNote(res);
                setNote(text);
                setHasNote(Boolean(text));
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(err);
                toast.error("Qeydi yükləmək mümkün olmadı.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, propertyId]);

    useEffect(() => {
        if (!open) setConfirmDeleteOpen(false);
    }, [open]);

    const onSave = async () => {
        const text = note.trim();
        if (!text) {
            toast.error("Qeyd boş ola bilməz.");
            return;
        }

        setSaving(true);
        try {
            const res = await saveCustomPropertyNote(propertyId, text);
            if (res && res.success === false) {
                toast.error(res.message || "Qeydi saxlamaq mümkün olmadı.");
                return;
            }
            toast.success(hasNote ? "Qeyd yeniləndi." : "Qeyd saxlanıldı.");
            queryClient.setQueryData(propertyNoteKey(propertyId), text);
            onNoteChange?.(true);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.message || "Qeydi saxlamaq mümkün olmadı.");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        setConfirmDeleteOpen(false);
        setDeleting(true);
        try {
            const res = await removeCustomPropertyNote(propertyId);
            if (res && res.success === false) {
                toast.error(res.message || "Qeydi silmək mümkün olmadı.");
                return;
            }
            toast.success("Qeyd silindi.");
            setNote("");
            setHasNote(false);
            queryClient.setQueryData(propertyNoteKey(propertyId), "");
            onNoteChange?.(false);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.message || "Qeydi silmək mümkün olmadı.");
        } finally {
            setDeleting(false);
        }
    };

    const toggleQuickNote = (text) => {
        setNote((prev) => {
            const idx = prev.indexOf(text);

            if (idx !== -1) {
                let before = prev.slice(0, idx).trimEnd();
                const after = prev
                    .slice(idx + text.length)
                    .replace(/^[.!?]?\s*/, "")
                    .trimStart();

                if (!after) {
                    before = before.replace(/[.!?]$/, "");
                    return before;
                }

                return before ? `${before} ${after}` : after;
            }

            let current = prev.trimEnd();
            if (!current) return text;

            if (!/[.!?]$/.test(current)) current += ".";

            const next = `${current} ${text}`;
            return next.length > MAX_NOTE_LENGTH ? prev : next;
        });
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    data-testid="property-note-dialog"
                    className="sm:max-w-2xl"
                >
                    <DialogHeader>
                        <DialogTitle>Elan üçün qeydinizi yazın</DialogTitle>
                        <DialogDescription>
                            {hasNote
                                ? "Bu elan üçün əvvəl yazdığınız qeydi dəyişə və ya silə bilərsiniz."
                                : "Bu qeyd yalnız sizə görünəcək."}
                        </DialogDescription>
                    </DialogHeader>

                    <div
                        className="flex flex-wrap gap-1.5"
                        data-testid="property-note-quick-list"
                    >
                        {QUICK_NOTES.map((text) => {
                            const selected = note.includes(text);

                            return (
                                <button
                                    key={text}
                                    type="button"
                                    onClick={() => toggleQuickNote(text)}
                                    disabled={busy}
                                    data-testid="property-note-quick-btn"
                                    aria-pressed={selected}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium transition disabled:opacity-60 ${selected
                                        ? "border-[#EA580C] bg-[#EA580C] text-white hover:bg-[#C2410C]"
                                        : "border-[#EA580C]/40 bg-[#EA580C]/10 text-black hover:bg-[#EA580C] hover:text-white"
                                        }`}
                                >
                                    {selected && <Check size={12} weight="bold" />}
                                    {text}
                                </button>
                            );
                        })}
                    </div>

                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        disabled={busy}
                        rows={10}
                        maxLength={MAX_NOTE_LENGTH}
                        placeholder={loading ? "Yüklənir..." : "Qeydinizi bura yazın..."}
                        data-testid="property-note-input"
                        className="w-full min-h-[220px] resize-y rounded-md border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                    />

                    <DialogFooter>
                        {hasNote ? (
                            <button
                                type="button"
                                onClick={() => setConfirmDeleteOpen(true)}
                                disabled={busy}
                                data-testid="property-note-delete"
                                className="px-4 py-2 rounded-md border border-red-500/50 text-red-600 text-sm font-semibold hover:bg-red-500/10 transition disabled:opacity-60"
                            >
                                {deleting ? "Silinir..." : "Sil"}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                disabled={busy}
                                className="px-4 py-2 rounded-md border border-border text-sm hover:bg-secondary transition disabled:opacity-60"
                            >
                                Ləğv et
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={busy}
                            data-testid="property-note-save"
                            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110 transition disabled:opacity-60"
                        >
                            {saving ? "Saxlanılır..." : "Yadda saxla"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
            >
                <AlertDialogContent data-testid="property-note-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Qeydi silmək istədiyinizdən əminsinizmi?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu qeyd həmişəlik silinəcək və geri qaytarıla bilməyəcək.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="property-note-delete-cancel">
                            Xeyr
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="property-note-delete-confirm-btn"
                            onClick={onDelete}
                            className="bg-red-600 text-white hover:bg-red-700"
                        >
                            Bəli, sil
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};